<script lang="ts">
import { computed, defineComponent, ref, type PropType } from 'vue';
import type { UICardDisplay } from '@legendary-arena/game-engine';
import { useEffectIntensity } from '../../vfx/effectIntensity';

/**
 * Reusable card tile — renders either a card image (when imageUrl is truthy)
 * or a text fallback (when imageUrl is falsy). Single rendering surface for
 * all play-surface cards per WP-178.
 */
export default defineComponent({
  name: 'CardTile',
  props: {
    display: {
      type: Object as PropType<UICardDisplay>,
      required: true,
    },
    size: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      required: false,
      default: 'md',
    },
    showCost: {
      type: Boolean,
      required: false,
      default: true,
    },
    interactive: {
      type: Boolean,
      required: false,
      default: false,
    },
    showLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * When true (hand tiles only, set by {@link HandRow}), the tile gets the
     * WP-699 hover lift — but only on a real pointer and only when the feel
     * layer's accessibility gate allows it. City / HQ / in-play tiles leave
     * this off and keep the plain interactive scale hover.
     */
    handLift: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Hover text that replaces the card name when set. Board tiles inside a gated
     * button pass the gate's disabled reason here.
     *
     * // why: D-24614 — this tile covers its whole button, and the browser shows
     * the innermost `title`, so the name on this div hid the button's disabled
     * reason (the heal lock, stage, and cost tooltips) on every board tile.
     * Null (the default, and an allowed gate) keeps the card name.
     */
    tooltip: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
  },
  setup(props) {
    // why: tracks broken image loads so the tile falls back to text mode
    // instead of rendering a black rectangle on the dark card background
    const imageLoadFailed = ref(false);

    // why: the WP-699 hover lift is a hand-tile-only affordance and must obey
    // the shipped feel-layer accessibility gate. shouldRender('particles')
    // already returns false at 'off' intensity and under reduced-motion; the
    // explicit prefersReducedMotion check mirrors the WP-699 locked value and
    // keeps the intent readable. A disabled (non-interactive) tile never lifts.
    const { prefersReducedMotion, shouldRender } = useEffectIntensity();
    const isLiftEnabled = computed(
      () =>
        props.handLift &&
        props.interactive &&
        shouldRender('particles') &&
        !prefersReducedMotion.value,
    );

    function hasImage(): boolean {
      return !!props.display.imageUrl && !imageLoadFailed.value;
    }

    function onImageError(): void {
      imageLoadFailed.value = true;
    }

    function shouldShowCostBadge(): boolean {
      return props.showCost && props.display.cost !== null;
    }

    return { hasImage, onImageError, shouldShowCostBadge, isLiftEnabled };
  },
});
</script>

<template>
  <div
    class="card-tile"
    :class="[
      `card-tile--${size}`,
      { 'card-tile--interactive': interactive, 'card-tile--lift-enabled': isLiftEnabled },
    ]"
    :title="tooltip ?? display.name"
    data-testid="card-tile"
    :data-card-ext-id="display.extId"
  >
    <!-- why: loading="lazy" reserves bandwidth for visible cards; off-screen
         card images load only when scrolled into view -->
    <img
      v-if="hasImage()"
      :src="display.imageUrl"
      :alt="display.name"
      loading="lazy"
      class="card-tile__image"
      data-testid="card-tile-image"
      @error="onImageError"
    />
    <div
      v-else
      class="card-tile__fallback"
      data-testid="card-tile-fallback"
    >
      <span class="card-tile__name">{{ display.name }}</span>
      <span
        v-if="display.cost !== null"
        class="card-tile__cost-text"
      >
        Cost: {{ display.cost }}
      </span>
    </div>
    <span
      v-if="hasImage() && shouldShowCostBadge()"
      class="card-tile__cost-badge"
      data-testid="card-tile-cost-badge"
    >
      {{ display.cost }}
    </span>
    <!-- why: card name + cost below the image so players can read the card
         identity without relying on hover tooltips or squinting at tiny art -->
    <div
      v-if="hasImage() && showLabel"
      class="card-tile__label"
      data-testid="card-tile-label"
    >
      <span class="card-tile__label-name">{{ display.name }}</span>
      <span v-if="shouldShowCostBadge()" class="card-tile__label-cost">
        {{ display.cost }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.card-tile {
  position: relative;
  aspect-ratio: 5 / 7;
  overflow: hidden;
  border-radius: 4px;
  background: var(--color-card-bg, #1a1a2e);
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-tile--sm {
  width: var(--card-width-sm);
}

.card-tile--md {
  width: var(--card-width-md);
}

.card-tile--lg {
  width: var(--card-width-lg);
}

.card-tile--interactive {
  cursor: pointer;
  transition: transform 0.15s ease;
}

.card-tile--interactive:hover {
  transform: scale(1.05);
}

/* WP-699 hover lift — hand tiles only, pointer devices only, gated in JS by the
   feel-layer accessibility contract (the `card-tile--lift-enabled` class is
   absent at 'off' intensity and under reduced-motion). transform + box-shadow +
   z-index only (no layout property animates; D-24365 hand-rolled CSS). */
@media (hover: hover) {
  .card-tile--lift-enabled {
    position: relative;
    z-index: 1;
    /* why: transform + shadow animate over 150ms, but z-index restores only
       AFTER that window (0s change, 150ms delay) so a card easing back down
       never drops beneath a neighbour mid-transition and clips. The :hover rule
       below removes the delay so the raise is immediate on enter. */
    transition:
      transform 0.15s ease-out,
      box-shadow 0.15s ease-out,
      z-index 0s linear 0.15s;
  }

  /* why: the lift is more specific than the plain interactive scale and is
     declared later, so on a hand tile it wins the transform on hover. */
  .card-tile--lift-enabled:hover {
    transform: translateY(-12px) scale(1.06);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.45);
    z-index: 10;
    transition:
      transform 0.15s ease-out,
      box-shadow 0.15s ease-out,
      z-index 0s;
  }
}

.card-tile__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.card-tile__fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.35rem;
  text-align: center;
  width: 100%;
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-sizing: border-box;
}

.card-tile__name {
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  color: #fff;
  text-transform: capitalize;
}

.card-tile__cost-text {
  font-size: 0.7rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #ffd700;
}

.card-tile__cost-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  padding: 0 0.2rem;
}

.card-tile__label {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  padding: 0.15rem 0.25rem;
  background: rgba(0, 0, 0, 0.65);
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

.card-tile__label-name {
  font-size: 0.6rem;
  font-weight: 600;
  line-height: 1.2;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.card-tile__label-cost {
  font-size: 0.55rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #ffd700;
  flex-shrink: 0;
}
</style>
